/**
 * timeTicks (#2543) — pure calendar-tick generation for a numeric (epoch-ms)
 * chart axis, plus the German label for each grain.
 *
 * On the categorical axis the three app charts use today, "ticks" are just a
 * subset of the row categories and recharts thins them with ``minTickGap``. A
 * true time axis has no rows to thin: the labels must land on real CALENDAR
 * boundaries — midnights, Mondays, firsts-of-month, New Year — otherwise the
 * axis reads as arbitrary offsets from whatever the window happens to start at.
 * This module picks the boundary family and spacing for a given span and emits
 * the timestamps; the caller hands them to ``<XAxis ticks=… interval={0} />``.
 *
 * Everything is UTC. The chart domain is UTC epoch ms (a row's ``ts`` is
 * midnight UTC of its ISO date), so deriving or formatting through the ambient
 * timezone would slide a tick onto the wrong calendar day for any user east or
 * west of UTC.
 *
 * ── How the grain is chosen ────────────────────────────────────────────────
 * Not by fixed span buckets but by a search over a ladder of
 * ``(grain, step)`` candidates, coarsening monotonically. The densest candidate
 * that stays at or under {@link MAX_TICKS} wins, preferring the count closest to
 * the target (~7 labels). Fixed buckets cannot hold a sensible label count: a
 * 30-day window bucketed to "week" yields only 4 Mondays, while the same window
 * at a 5-day step yields a comfortable 6 — the search finds that automatically
 * and keeps every preset (1W…Max) inside 5…9 labels at every pan offset.
 *
 * The one span the band cannot cover is the ``1T`` preset: two days simply do
 * not CONTAIN five day boundaries. Rather than invent sub-day ticks for a single
 * preset, the generator emits every day boundary available (2…3 labels).
 *
 * Pure — no React, no recharts, no Date mutation — so the calendar invariants
 * are unit-tested directly.
 */

/** The calendar family a tick sits on. Also selects the label format. */
export type TickGrain = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface TimeTicks {
  grain: TickGrain;
  /**
   * How many ``grain`` units apart consecutive ticks are — ``grain: 'month'``
   * with ``step: 2`` is every second month, ``grain: 'quarter'`` with ``step: 2``
   * every second QUARTER (i.e. six months). Exposed because "evenly spaced" for
   * the calendar grains means even in CALENDAR units, not in ms — a consumer
   * sizing buckets (#2546's candles) needs the unit AND the multiple, so the
   * multiple must always be counted in the unit it is paired with.
   */
  step: number;
  /** Tick positions in epoch ms, ascending, inside the requested domain. */
  ticks: number[];
}

const DAY_MS = 86_400_000;
/** ~7 labels reads as a comfortable axis at every width the app charts use. */
const TARGET_TICKS = 7;
/**
 * Above this the labels collide even with the widest plot.
 *
 * Exported since HOF-96, because it is now the DEFAULT of an option rather than
 * an absolute: `timeAxisTicks(lo, hi, { max })` lets one caller that has
 * measured its own plot ask for a denser ladder. The constant itself does not
 * move — it is sized for the widest labels this module emits (`01. Mär`,
 * `Mär 26`), and a caller whose labels are half that wide is an exception, not
 * a reason to weaken a cap every other caller relies on.
 */
export const MAX_TICKS = 9;
/** Epoch day 0 (1970-01-01) was a Thursday; Monday is epoch day 4. */
const MONDAY_EPOCH_DAY = 4;

interface Candidate {
  grain: TickGrain;
  step: number;
}

/**
 * Coarsening ladder. Day/week steps are the classic 1-2-3-5 "nice number"
 * progression; month steps stay aligned to January so a 3-month step IS the
 * calendar quarters and a 6-month step the half-years.
 */
const CANDIDATES: Candidate[] = [
  { grain: 'day', step: 1 },
  { grain: 'day', step: 2 },
  { grain: 'day', step: 3 },
  { grain: 'day', step: 5 },
  { grain: 'week', step: 1 },
  { grain: 'week', step: 2 },
  { grain: 'month', step: 1 },
  { grain: 'month', step: 2 },
  { grain: 'quarter', step: 1 },
  { grain: 'quarter', step: 2 },
  { grain: 'year', step: 1 },
  { grain: 'year', step: 2 },
  { grain: 'year', step: 5 },
  { grain: 'year', step: 10 },
];

/** Ticks every ``step`` days, aligned to epoch-day multiples of ``step``. */
function dayTicks(lo: number, hi: number, step: number): number[] {
  const firstDay = Math.ceil(lo / DAY_MS);
  const start = firstDay + ((step - (firstDay % step)) % step);
  const out: number[] = [];
  for (let d = start; d * DAY_MS <= hi; d += step) out.push(d * DAY_MS);
  return out;
}

/** Ticks on every ``step``-th Monday. */
function weekTicks(lo: number, hi: number, step: number): number[] {
  const firstDay = Math.ceil(lo / DAY_MS);
  // Weeks numbered from the first Monday of the epoch, so a 2-week step keeps a
  // stable parity as the window pans instead of re-anchoring on the viewport.
  const weeksFromAnchor = Math.ceil((firstDay - MONDAY_EPOCH_DAY) / 7);
  let week = weeksFromAnchor + ((step - (weeksFromAnchor % step)) % step);
  const out: number[] = [];
  for (;;) {
    const ts = (MONDAY_EPOCH_DAY + week * 7) * DAY_MS;
    if (ts > hi) break;
    if (ts >= lo) out.push(ts);
    week += step;
  }
  return out;
}

/** Ticks on the 1st of every ``step``-th month, aligned to January. */
function monthTicks(lo: number, hi: number, step: number): number[] {
  const from = new Date(lo);
  let year = from.getUTCFullYear();
  let month = from.getUTCMonth();
  month += (step - (month % step)) % step;
  const out: number[] = [];
  for (;;) {
    const ts = Date.UTC(year + Math.floor(month / 12), month % 12, 1);
    if (ts > hi) break;
    if (ts >= lo) out.push(ts);
    month += step;
    if (month >= 12) {
      year += Math.floor(month / 12);
      month %= 12;
    }
  }
  return out;
}

/** Ticks on 1 January of every ``step``-th year. */
function yearTicks(lo: number, hi: number, step: number): number[] {
  let year = new Date(lo).getUTCFullYear();
  year += (step - (((year % step) + step) % step)) % step;
  const out: number[] = [];
  for (;;) {
    const ts = Date.UTC(year, 0, 1);
    if (ts > hi) break;
    if (ts >= lo) out.push(ts);
    year += step;
  }
  return out;
}

function ticksFor(c: Candidate, lo: number, hi: number): number[] {
  switch (c.grain) {
    case 'day':
      return dayTicks(lo, hi, c.step);
    case 'week':
      return weekTicks(lo, hi, c.step);
    case 'month':
      return monthTicks(lo, hi, c.step);
    case 'quarter':
      // ``step`` counts QUARTERS; the generator counts months.
      return monthTicks(lo, hi, c.step * 3);
    case 'year':
      return yearTicks(lo, hi, c.step);
  }
}

/**
 * The tick family for the visible domain ``[lo, hi]`` (epoch ms, inclusive).
 *
 * Returns the densest ladder candidate at or under {@link MAX_TICKS} whose label
 * count is closest to ``opts.target`` (default ~7). Ties prefer the denser
 * candidate. A degenerate, inverted or non-finite domain yields no ticks — the
 * caller can then leave recharts to its automatic ticks.
 */
export function timeAxisTicks(lo: number, hi: number, opts?: { target?: number; max?: number }): TimeTicks {
  const target = opts?.target ?? TARGET_TICKS;
  // HOF-96 — an opt-in ceiling. `target` almost always has to move with it: the
  // search picks the candidate CLOSEST to the target, so a raised `max` on its
  // own still loses to whatever sits nearer 7.
  const max = opts?.max ?? MAX_TICKS;
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
    return { grain: 'day', step: 1, ticks: [] };
  }

  let best: TimeTicks | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const c of CANDIDATES) {
    const ticks = ticksFor(c, lo, hi);
    if (ticks.length === 0 || ticks.length > max) continue;
    const score = Math.abs(ticks.length - target);
    if (score < bestScore) {
      bestScore = score;
      best = { grain: c.grain, step: c.step, ticks };
    }
  }
  // No candidate qualified — either a multi-century domain that exhausts the
  // ladder (every family still too dense), a sub-day one containing no calendar
  // boundary at all, or a caller's `max` too small for any family to fit. Try the coarsest family; if that is empty too,
  // report the SAME "no ticks" shape as the degenerate-input guard above, so a
  // consumer keying ``formatTimeTick`` off ``grain`` never sees an arbitrary one.
  if (!best) {
    const coarsest = CANDIDATES[CANDIDATES.length - 1];
    const ticks = ticksFor(coarsest, lo, hi);
    if (ticks.length === 0) return { grain: 'day', step: 1, ticks: [] };
    return { grain: coarsest.grain, step: coarsest.step, ticks };
  }
  return best;
}

/**
 * Formats a tick for its grain, German, UTC.
 *
 * Day/week reuse the app's existing axis format (``toLocaleDateString('de-DE',
 * {day: '2-digit', month: 'short'})``) so a numeric axis reads identically to
 * the categorical one it replaces. Month labels stay bare (``Aug``) except in
 * January, where the 2-digit year rides along to anchor the year turnover;
 * quarter labels always carry it, since they are far enough apart that a bare
 * month is ambiguous across years.
 */
export function formatTimeTick(ts: number, grain: TickGrain): string {
  const d = new Date(ts);
  const shortMonth = d.toLocaleDateString('de-DE', { month: 'short', timeZone: 'UTC' });
  const yy = String(d.getUTCFullYear() % 100).padStart(2, '0');
  switch (grain) {
    case 'day':
    case 'week':
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', timeZone: 'UTC' });
    case 'month':
      return d.getUTCMonth() === 0 ? `${shortMonth} ${yy}` : shortMonth;
    case 'quarter':
      return `${shortMonth} ${yy}`;
    case 'year':
      return String(d.getUTCFullYear());
  }
}

/**
 * The text metrics a tick-label collision depends on. Supplied by the caller,
 * because they belong to the AXIS that renders the labels, not to the generator
 * that places them: font size is an `<XAxis tick={{fontSize}}>` prop, and the
 * blank a reader needs between two labels is a design choice.
 */
export interface TickLabelMetrics {
  /** Font size the axis renders tick labels at, px. */
  fontPx: number;
  /**
   * Advance width of one character as a fraction of the font size.
   *
   * An approximation, and deliberately a conservative one: there is no way to
   * measure text under jsdom, and the alternative to a stated over-estimate is
   * an unstated assumption. ~0.62 em is wider than any digit or lower-case
   * letter in the app's sans stack at 11–12 px, so a ladder this function
   * clears has margin rather than a coin flip.
   */
  charEm: number;
  /** Blank two neighbouring labels must keep between them, px. */
  minGapPx: number;
}

/**
 * The narrowest plot width (px) at which `ticks` can be labelled without two
 * neighbours overlapping — 0 when there is nothing to collide.
 *
 * The model: each label is centred on its tick, the domain maps linearly onto
 * the plot width, and a pair clears when the distance between their centres
 * carries half of each label plus {@link TickLabelMetrics.minGapPx} of blank.
 *
 * **The TIGHTEST pair decides, never the average.** On a calendar ladder the two
 * are far apart: February is three days shorter than March, and the January
 * label carries the year (`Jan 26`) at twice the width of its neighbours, so the
 * pair straddling New Year needs a third more room than mean spacing suggests.
 * A width derived from the average is a width at which the axis still collides,
 * in one specific and highly visible place.
 *
 * Pure, so a view can assert its own pinned constant against it — which is the
 * point: it turns "480 px looked about right" into a number a font change can
 * falsify. See `MONTHLY_TICKS_MIN_PLOT_PX` in `PriceChart.tsx`.
 */
export function minPlotPxForTicks(
  ticks: readonly number[],
  grain: TickGrain,
  domain: { lo: number; hi: number },
  metrics: TickLabelMetrics,
): number {
  const span = domain.hi - domain.lo;
  if (ticks.length < 2 || !(span > 0)) return 0;
  const widthOf = (ts: number) => formatTimeTick(ts, grain).length * metrics.charEm * metrics.fontPx;
  let needed = 0;
  for (let i = 1; i < ticks.length; i += 1) {
    const gapFraction = (ticks[i] - ticks[i - 1]) / span;
    if (!(gapFraction > 0)) continue;
    const centreToCentrePx = (widthOf(ticks[i - 1]) + widthOf(ticks[i])) / 2 + metrics.minGapPx;
    needed = Math.max(needed, centreToCentrePx / gapFraction);
  }
  return needed;
}
